import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import InputMask from "react-input-mask";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const formSchema = z.object({
  name: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
  email: z.string().email("Email inválido"),
  phone: z.string().min(10, "Telefone inválido"),
  capital: z.string().min(1, "Selecione uma opção"),
});

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      capital: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-pipefy-card', {
        body: values
      });

      if (error) {
        // Evitar logar PII; manter apenas informação de erro
        console.error('Erro ao enviar para o Pipefy:', {
          name: (error as any)?.name,
          message: (error as any)?.message,
          status: (error as any)?.context?.status,
        });

        const status = (error as any)?.context?.status;
        const message = (error as any)?.message || 'Erro ao enviar formulário';
        toast.error(status ? `${message} (HTTP ${status})` : message);
        return;
      }

      // Erro retornado pela própria função (com status 200 em alguns cenários)
      if ((data as any)?.error) {
        const details = (data as any)?.details;
        const detailsText = Array.isArray(details)
          ? details.map((d) => (typeof d === 'string' ? d : d?.message || JSON.stringify(d))).join(' | ')
          : undefined;
        toast.error(detailsText ? `${(data as any).error}: ${detailsText}` : (data as any).error);
        return;
      }

      form.reset();
      navigate('/obrigado');
      
    } catch (error) {
      console.error('Erro ao processar formulário:', {
        message: error instanceof Error ? error.message : String(error),
      });
      toast.error(error instanceof Error ? error.message : "Erro ao enviar formulário.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section id="contato" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-muted/20">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
            Entre em <span className="bg-gradient-primary bg-clip-text text-transparent">Contato</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Preencha o formulário e nosso time irá entrar em contato
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-elegant">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-semibold text-foreground">Nome</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Seu nome completo" 
                        {...field} 
                        className="h-12 bg-background/50 border-2 focus-visible:border-primary transition-colors"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-semibold text-foreground">Email</FormLabel>
                    <FormControl>
                      <Input 
                        type="email" 
                        placeholder="seu@email.com" 
                        {...field} 
                        className="h-12 bg-background/50 border-2 focus-visible:border-primary transition-colors"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-semibold text-foreground">Telefone</FormLabel>
                    <FormControl>
                      <InputMask
                        mask="(99) 9 9999-9999"
                        value={field.value}
                        onChange={field.onChange}
                      >
                        {(inputProps: any) => (
                          <Input 
                            {...inputProps}
                            type="tel"
                            placeholder="(00) 0 0000-0000" 
                            className="h-12 bg-background/50 border-2 focus-visible:border-primary transition-colors"
                          />
                        )}
                      </InputMask>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="capital"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-semibold text-foreground">Capital disponível</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12 bg-background/50 border-2 focus:border-primary transition-colors">
                          <SelectValue placeholder="Selecione uma opção" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="ate-10k">Até 10 mil</SelectItem>
                        <SelectItem value="10-50k">Entre 10 e 50 mil</SelectItem>
                        <SelectItem value="50-100k">Entre 50 e 100 mil</SelectItem>
                        <SelectItem value="100-300k">Entre 100 e 300 mil</SelectItem>
                        <SelectItem value="acima-300k">Acima de 300 mil</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button 
                type="submit" 
                className="w-full" 
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Enviando..." : "Enviar"}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
